import { useEffect } from 'react';
import { isIOS, isStandalonePWA, hasWebPushSupport, getPushPermissionState } from '../../services/platformService';
import { useAuthStore } from '../../store/useAuthStore';
import { openPushPermissionModal, wasPwaPromptShown } from '../../services/pushPermissionModalService';
import { logDebug } from '../../services/loggerService';

/**
 * Renderless component that pops the push value modal the first time an
 * authenticated user launches HomePlated as an installed iOS PWA in
 * standalone mode.
 *
 * Why this exists: at registration time on iOS-web (Safari tab), we can't
 * ask for push permission — the API doesn't exist there. We show the
 * "Add to Home Screen" instructions modal instead. Once the user installs
 * and launches the PWA, this trigger detects that we're now standalone +
 * have permission API access + haven't asked yet, and opens the standard
 * value-pitch modal whose "Enable" button satisfies iOS's user-gesture
 * requirement for requestPermission().
 *
 * One-shot: persists `hp_push_pwa_prompt_shown` on close (set by the modal
 * itself when opened with `fromFirstLaunch: true`). After that, users in
 * the `default` state can re-trigger from Profile → Settings; denied/
 * granted users never see this again.
 *
 * Waits for auth restore so we don't fire for unauthenticated users (the
 * modal is meaningful only post-signin).
 */
export const PushFirstLaunchTrigger: React.FC = () => {
  const sessionReady = useAuthStore((s) => s.sessionStatus === 'ready');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    if (!sessionReady) return;
    if (!isAuthenticated) return;
    if (!isStandalonePWA()) return;
    if (!isIOS()) return;
    if (!hasWebPushSupport()) return;
    if (getPushPermissionState() !== 'default') return;

    if (wasPwaPromptShown()) return;

    logDebug('PushFirstLaunchTrigger: opening modal for iOS PWA first launch');
    openPushPermissionModal({ fromFirstLaunch: true });
  }, [sessionReady, isAuthenticated]);

  return null;
};
